/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v3";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import * as types from "../../types/primitives.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  ConditionExpression,
  ConditionExpression$inboundSchema,
  ConditionExpression$Outbound,
  ConditionExpression$outboundSchema,
} from "./conditionexpression.js";
import {
  LabelConfig,
  LabelConfig$inboundSchema,
  LabelConfig$Outbound,
  LabelConfig$outboundSchema,
} from "./labelconfig.js";

/**
 * Defines a state of a label.
 */
export type LabelState = {
  /**
   * Defines a condition expression. Can be a single condition, a group of conditions, or nested groups. Exactly one of 'condition', 'condition_group', or 'nested_expression' must be specified.
   */
  conditionExpression?: ConditionExpression | undefined;
  /**
   * Defines a label that is overlaid on top of an animatic.
   */
  config: LabelConfig;
  /**
   * Whether this state is the default state. If this is true, conditions are not required. For readability, these states should be at the end of the array.
   */
  isDefault?: boolean | undefined;
  /**
   * The priority of the state. There is no constraint on overlapping conditions, so this field allows user to set priority.
   */
  priority?: number | undefined;
  /**
   * The name of the state
   */
  stateName: string;
};

/** @internal */
export const LabelState$inboundSchema: z.ZodType<
  LabelState,
  z.ZodTypeDef,
  unknown
> = z.object({
  condition_expression: types.optional(ConditionExpression$inboundSchema),
  config: LabelConfig$inboundSchema,
  is_default: types.boolean().default(false),
  priority: types.number().default(0),
  state_name: types.string(),
}).transform((v) => {
  return remap$(v, {
    "condition_expression": "conditionExpression",
    "is_default": "isDefault",
    "state_name": "stateName",
  });
});
/** @internal */
export type LabelState$Outbound = {
  condition_expression?: ConditionExpression$Outbound | undefined;
  config: LabelConfig$Outbound;
  is_default: boolean;
  priority: number;
  state_name: string;
};

/** @internal */
export const LabelState$outboundSchema: z.ZodType<
  LabelState$Outbound,
  z.ZodTypeDef,
  LabelState
> = z.object({
  conditionExpression: ConditionExpression$outboundSchema.optional(),
  config: LabelConfig$outboundSchema,
  isDefault: z.boolean().default(false),
  priority: z.number().int().default(0),
  stateName: z.string(),
}).transform((v) => {
  return remap$(v, {
    conditionExpression: "condition_expression",
    isDefault: "is_default",
    stateName: "state_name",
  });
});

export function labelStateToJSON(labelState: LabelState): string {
  return JSON.stringify(LabelState$outboundSchema.parse(labelState));
}
export function labelStateFromJSON(
  jsonString: string,
): SafeParseResult<LabelState, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => LabelState$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'LabelState' from JSON`,
  );
}
